<template>
  <div class="w-full flex" v-loading="loading">
    <div class="w-4/12 p-2">
      <div class="border rounded-md px-4">
        <el-form :model="form" ref="form">
          <!-- Report Period Type -->
          <el-form-item prop="dataForm">
            <h5 class="text-base font-bold text-black mt-2">Report Period</h5>
            <el-select
              v-model="form.dataForm"
              @change="onChangePicker"
              placeholder="Select Report Period"
              class="w-full"
            >
              <el-option
                v-for="item in dateOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <!-- Period Range -->
          <h5 class="text-base font-bold text-black my-2">Period Range</h5>
          <div class="flex justify-between">
            <el-form-item prop="startDate">
              <el-date-picker
                v-model="form.startDate"
                :picker-options="datePickerOptions"
                :type="periodDatePickerType"
                placeholder="Start Period"
                value-format="yyyy-MM-dd"
                @change="validateDate"
                class="mr-1"
                style="width: 98%;"
              />
            </el-form-item>
            <el-form-item prop="endDate">
              <el-date-picker
                v-model="form.endDate"
                :picker-options="datePickerOptions"
                :type="periodDatePickerType"
                placeholder="End Period"
                value-format="yyyy-MM-dd"
                @change="validateDate"
                class="ml-1"
                style="width: 98%;"
              />
            </el-form-item>
          </div>
          <!-- Location Dropdown -->
          <el-form-item>
            <h5 class="text-base font-bold text-black">Location</h5>
            <el-select
              v-model="form.location"
              placeholder="Select Location"
              class="w-full"
            >
              <el-option
                v-for="item in locationOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
        </el-form>

        <!-- Submit button -->
        <div class="flex mt-4 mb-4 justify-end">
          <el-button type="primary" @click="submit">Submit</el-button>
        </div>
      </div>
    </div>
    <div class="w-8/12 p-2">
      <div class="border rounded-md p-3">
        <div v-if="chartBarVisible">
          <div class="flex justify-between">
            <div>
              <img
                src="@/assets/images/logo-corp.png"
                alt="logo-corp"
                class="h-16"
              />
            </div>
            <div class="flex">
              <el-tooltip
                class="item"
                effect="dark"
                content="Download"
                placement="top-start" >
                <el-dropdown @command="downloadReport" trigger="click">
                  <div
                    class="rounded-full h-10 w-10 bg-blue-600 flex items-center justify-center text-white hover:opacity-75"
                  >
                    <i class="el-icon-download text-xl"></i>
                  </div>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item :command="'pdf'">PDF</el-dropdown-item>
                    <el-dropdown-item :command="'excel'">Excel</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </el-tooltip>
            </div>
          </div>
          <!-- Chart -->
          <div class="bg-white w-full flex justify-center">
            <stacked-chart
              ref="canvas"
              style="width: 100%;"
              :height="200"
              :chart-data="dataCollectionReport"
              :options="chartOptionsReport"
            />
          </div>
          <!-- End of Chart -->
          <!-- Severity Table -->
          <div class="mt-4">
            <div
              class="font-bold w-32 p-1 rounded-t border border-b-0 text-center text-gray-800"
              style="background: #f3f4f7;"
            >
              Severity Count
            </div>
            <vue-good-table
              :columns="severityColumns"
              :rows="severityRows"
              :line-numbers="true"
              styleClass="vgt-table bordered striped condensed"
            >
              <template slot="table-row" slot-scope="props">
                <span
                  :id="`${props.column.field}-${props.index}`"
                  class="text-sm"
                >
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </div>
          <!-- End of Severity Table -->
          <!-- Alarm List Table -->
          <div class="mt-4">
            <div class="flex justify-between items-center">
              <div
                class="font-bold w-32 p-1 rounded-t border border-b-0 text-center text-gray-800"
                style="background: #f3f4f7;"
              >
                Alarm List
              </div>
              <div class="flex items-center">
                <input
                  type="checkbox"
                  name="alarmTableCheck"
                  v-model="isAlarmTableIncludedOnDownload"
                />
                <label for="alarmTableCheck" class="text-xs ml-1"
                  >Put table on downloaded file</label
                >
              </div>
            </div>
            <vue-good-table
              :columns="alarmColumns"
              :rows="alarmRows"
              :line-numbers="true"
              styleClass="vgt-table bordered striped condensed"
              :pagination-options="{
                enabled: true,
                mode: 'records',
                perPage: 10
              }"
            >
              <template slot="table-row" slot-scope="props">
                <span
                  :id="`${props.column.field}-${props.index}`"
                  class="text-sm"
                >
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </div>
          <!-- End of Alarm List Table -->
        </div>
        <div v-else class="h-64 text-center">
          <i class="el-icon-info"></i> Please do fulfill fields on the left side
          first to display report data
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import imagesLib from './images.json'
import StackedChart from '@/components/Chart/StackedChart.js'
import moment from 'moment'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
pdfMake.vfs = pdfFonts.pdfMake.vfs
import FileSaver from 'file-saver'

export default {
  components: {
    StackedChart
  },
  data() {
    return {
      loading: false,
      chartBarVisible: false,
      chartBase64: '',
      dataCollectionReport: {
        labels: [],
        datasets: []
      },
      chartOptionsReport: {
        plugins: {
          datalabels: {
            display: true,
            color: 'white'
          }
        },
        bezierCurve: false,
        animation: {
          onComplete: this.chartOnLoadComplete
        },
        title: {
          display: true,
          text: 'Alarm Severity Chart',
          fontSize: 17
        },
        legend: {
          // display: false,
          labels: {
            fontColor: 'black',
            usePointStyle: true
          },
          position: 'bottom'
        },
        scales: {
          yAxes: [
            {
              stacked: true,
              gridLines: { color: '#607d8b' },
              ticks: {
                fontColor: 'black',
                // min: 0,
                suggestedMin: 15
              },
              scaleLabel: {
                display: true,
                labelString: ''
              }
            }
          ],
          xAxes: [
            {
              stacked: true,
              gridLines: { color: '' },
              ticks: {
                fontColor: 'black'
              }
            }
          ]
        }
      },
      form: {
        startDate: '',
        endDate: '',
        dataForm: 'daily_avg',
        location: ''
      },
      dateOptions: [
        {
          value: 'daily_avg',
          label: 'Daily'
        },
        {
          value: 'monthly_avg',
          label: 'Monthly'
        },
        {
          value: 'yearly_avg',
          label: 'Yearly'
        }
      ],
      locationOptions: [
        { value: 'PDC', label: 'PENGERANG DATA CENTRE' },
        { value: 'THMO', label: 'THMO SERVER ROOM ' }
      ],
      sensorTypeOptions: [],
      datePickerOptions: '',
      periodDatePickerType: 'date',
      checkboxes: [],
      dropdownReport: [],
      tableData: [],
      tableHeaders: [],
      severityRows: [],
      alarmColumns: [
        {
          label: 'Active Time',
          field: 'activeTime',
          type: 'date',
          dateInputFormat: 'yyyy-MM-dd',
          dateOutputFormat: 'dd-MMM-yyyy'
        },
        {
          label: 'Severity',
          field: 'severity'
        },
        {
          label: 'DCE Name',
          field: 'dceName'
        },
        {
          label: 'Location',
          field: 'location'
        },
        {
          label: 'State',
          field: 'state'
        }
      ],
      alarmRows: [],
      isAlarmTableIncludedOnDownload: true
    }
  },
  computed: {
    dateOutputFormatSeverity: function () {
      if (this.form.dataForm === 'yearly_avg') return 'yyyy'
      else if (this.form.dataForm === 'monthly_avg') return 'MMM yyyy'
      else return 'dd MMM yyyy'
    },
    severityColumns() {
      return [
        {
          label: 'Date',
          field: 'date',
          type: 'date',
          dateInputFormat: 'yyyy-MM-dd',
          dateOutputFormat: this.dateOutputFormatSeverity
        },
        {
          label: 'Critical',
          field: 'critical',
          type: 'number'
        },
        {
          label: 'Warning',
          field: 'warning',
          type: 'number'
        },
        {
          label: 'Informational',
          field: 'informational',
          type: 'number'
        }
      ]
    }
  },
  methods: {
    createChart(data = {}) {
      this.dataCollectionReport = {
        labels: data.labels,
        datasets: [
          {
            label: 'Critical',
            backgroundColor: '#c62828',
            data: data.criticals
          },
          {
            label: 'Warning',
            backgroundColor: '#EE9E3D',
            data: data.warnings
          },
          {
            label: 'Informational',
            backgroundColor: '#52A2EC',
            data: data.informationals
          }
        ]
      }

      this.chartBarVisible = true
    },
    async submit() {
      this.loading = true

      try {
        // Validation
        if (this.form.startDate === '' || this.form.endDate === '') {
          this.$message.error('Please choose date correctly')
          this.loading = false
          return
        } else if (this.form.location === '') {
          this.$message.error('Please choose location first')
          this.loading = false
          return
        }
        // End of validation

        const res = await this.$axios.get(
          `Alarms/GetAlarmSummaryAlarmsWithLocation?dataForm=${this.form.dataForm}&startDate=${this.form.startDate}&endDate=${this.form.endDate}&location=${this.form.location}`
        )

        const formatDate = (date) => moment(date).format('yyyy-MM-DD')

        // Collecting severity table
        this.severityRows = []
        res.data.forEach((item) => {
          this.severityRows.push({
            date: formatDate(item.DateData),
            critical: item.DetailALarm.CountSeverity.CRITICAL,
            warning: item.DetailALarm.CountSeverity.WARNING,
            informational: item.DetailALarm.CountSeverity.INFORMATIONAL
          })
        })

        // collecting alarm list table
        let alarmRows = []
        res.data.forEach((item) => {
          item.ListAlarm.forEach((item) => {
            alarmRows.push({
              activeTime: formatDate(item.ActiveTime),
              severity: item.Severity,
              dceName: item.DceName,
              location: item.DceLocation,
              state: item.State
            })
          })
        })
        this.alarmRows = alarmRows

        // Collecting chart data
        let chartData = { labels: [] }
        chartData.labels = res.data.map((item) =>
          moment(item.DateData).format('DD-MM-yyyy')
        )
        chartData.criticals = res.data.map(
          (item) => item.DetailALarm.CountSeverity.CRITICAL
        )
        chartData.warnings = res.data.map(
          (item) => item.DetailALarm.CountSeverity.WARNING
        )
        chartData.informationals = res.data.map(
          (item) => item.DetailALarm.CountSeverity.INFORMATIONAL
        )

        this.createChart(chartData)

        this.chartBarVisible = true
      } catch (err) {
        console.log(err)
        this.loading = false
      }

      this.loading = false
    },
    onChangePicker() {
      if (this.form.dataForm == 'monthly_avg')
        this.periodDatePickerType = 'month'
      else if (this.form.dataForm == 'yearly_avg')
        this.periodDatePickerType = 'year'
      else this.periodDatePickerType = 'date'

      this.form.startDate = ''
      this.form.endDate = ''
    },
    validateDate() {
      if (this.form.startDate != '' && this.form.endDate != '') {
        let temp1 = new Date(this.form.startDate)
        let temp2 = new Date(this.form.endDate)
        if (temp1 > temp2) {
          this.form.endDate = ''
          this.$message.error('End Period must be greater than Start Period')
        }
      }
    },
    chartOnLoadComplete() {
      try {
        const chartBase64 = this.$refs.canvas.$refs.canvas.toDataURL()
        this.chartBase64 = chartBase64
      } catch (err) {
        console.log(err)
      }
    },
    printToPDF() {
      // severity format date
      let severityFormatDate = 'DD MMM YYYY'
      if (this.form.dataForm === 'monthly_avg') severityFormatDate = 'MMM YYYY'
      else if (this.form.dataForm === 'yearly_avg') severityFormatDate = 'YYYY'

      try {
        // Set alarm list table headers
        const alarmListTableHeaders = [
          ...this.alarmColumns.map((item) => {
            return {
              text: item.label,
              alignment: 'center',
              style: 'tableHeader'
            }
          })
        ]
        // Set headers of alarm list table
        let alarmListTableBody = [
          [...alarmListTableHeaders.map((item) => item)]
        ]
        // Set rows of alarm list table
        this.alarmRows.forEach((item) => {
          alarmListTableBody.push([
            {
              text: moment(item.activeTime).format('DD-MMM-YYYY'),
              alignment: 'right',
              style: 'textSmall'
            },
            { text: item.severity, alignment: 'left', style: 'textSmall' },
            { text: item.dceName, alignment: 'left', style: 'textSmall' },
            { text: item.location, alignment: 'left', style: 'textSmall' },
            { text: item.state, alignment: 'left', style: 'textSmall' }
          ])
        })

        // Set severity table headers
        const severityTableHeaders = [
          ...this.severityColumns.map((item) => {
            return {
              text: item.label,
              alignment: 'center',
              style: 'tableHeader'
            }
          })
        ]
        // Set headers of severity table
        let severityTableBody = [[...severityTableHeaders.map((item) => item)]]
        // Set rows of severity table
        this.severityRows.forEach((item) => {
          severityTableBody.push([
            {
              text: moment(item.date).format(severityFormatDate),
              alignment: 'right',
              style: 'textSmall'
            },
            { text: item.critical, alignment: 'right', style: 'textSmall' },
            { text: item.warning, alignment: 'right', style: 'textSmall' },
            { text: item.informational, alignment: 'right', style: 'textSmall' }
          ])
        })

        // format label header
        let labelHeaderDateFormat = 'DD MMM yyyy'
        if (this.form.dataForm === 'monthly_avg')
          labelHeaderDateFormat = 'MMM yyyy'
        else if (this.form.dataForm === 'yearly_avg')
          labelHeaderDateFormat = 'yyyy'

        const docDefinition = {
          styles: {
            textSmall: {
              fontSize: 10
            },
            tableHeader: {
              bold: true,
              fontSize: 10
            },
            generatedLabel: {
              fontSize: 8,
              color: '#A0AFC0'
            },
            title: {
              bold: true
            },
            fontBold: {
              bold: true
            },
            hidden: {
              display: 'none'
            }
          },
          content: [
            {
              alignment: 'justify',
              columns: [
                {
                  style: 'generatedLabel',
                  text: `Generated on ${moment().format('DD-MMM-YYYY')}`
                },
                {
                  image: 'logoCorp',
                  width: '70',
                  alignment: 'left'
                }
              ]
            },
            {
              alignment: 'center',
              style: 'title',
              text: '\n ALARM REPORT \n'
            },
            {
              alignment: 'center',
              style: 'textSmall',
              text: `Period: ${moment(this.form.startDate).format(
                labelHeaderDateFormat
              )} to ${moment(this.form.endDate).format(labelHeaderDateFormat)}`
            },
            '\n\n',
            {
              image: 'chart',
              width: '500',
              alignment: 'center'
            },
            '\n\n\n',
            {
              text: 'Severity Count',
              style: 'fontBold'
            },
            {
              table: {
                headerRows: 1,
                widths: ['*', '*', '*', '*'],
                body: [...severityTableBody]
              },
              layout: {
                fillColor: function (rowIndex, node, columnIndex) {
                  return rowIndex % 2 === 0 ? '#f5f5f5' : null
                }
              }
            },
            // check if isAlarmTableIncludedOnDownload is true
            '\n\n',
            this.isAlarmTableIncludedOnDownload
              ? {
                  text: 'Alarm List',
                  style: 'fontBold'
                }
              : '',
            // end of isAlarmTableIncludedOnDownload checking
            // check if isAlarmTableIncludedOnDownload is true
            this.isAlarmTableIncludedOnDownload
              ? {
                  table: {
                    headerRows: 1,
                    widths: ['*', '*', '*', '*', '*'],
                    body: [...alarmListTableBody]
                  },
                  layout: {
                    fillColor: function (rowIndex, node, columnIndex) {
                      return rowIndex % 2 === 0 ? '#f5f5f5' : null
                    }
                  }
                }
              : ''
            // end of isAlarmTableIncludedOnDownload checking
          ],
          images: {
            chart: this.chartBase64,
            logoCorp: imagesLib.logoCorp
          }
        }

        pdfMake
          .createPdf(docDefinition)
          .download(`Alarm Report ${moment().format('DD/MM/YYYY')}.pdf`)
      } catch (err) {
        console.log('error printToPDF', err)
      }
    },
    async downloadExcelReport() {
      const data = {
        dataForm: this.form.dataForm,
        // typeSensor: this.form.sensorType,
        startDate: this.form.startDate,
        endDate: this.form.endDate,
        location: this.form.location,
        SubDeviceName: []
      }

      const fileName = `Alarm Report ${moment().format('DD/MM/YYYY')}.xlsx`

      await this.$axios
      .post('ReportPDFetc/DownloadAlarmReport', data, {
        responseType: 'blob'
      })
      .then((res) => {
        FileSaver.saveAs(res.data, fileName)
      })
      .catch(err => {
        this.$message('Failed to download, please try again.')
        console.log(err)
      })
    },
    downloadReport(command) {
      if (command === 'pdf') this.printToPDF()
      else this.downloadExcelReport()
    }
  }
}
</script>
